import type { Locale } from './LanguageContext';

/**
 * Locale-aware number/date/currency formatting via the browser's built-in
 * `Intl` APIs - no extra dependency, correct grouping/decimal separators and
 * date ordering for `ru` without hand-rolling either. Only ever applied to
 * genuinely locale-safe values (display formatting of a number/date/money
 * amount already computed server-side) - never to domain enum values, IDs,
 * or anything that round-trips back to the API.
 */

const INTL_LOCALE: Record<Locale, string> = { en: 'en-US', ru: 'ru-RU' };

export function formatDate(value: string | Date, locale: Locale, opts?: Intl.DateTimeFormatOptions): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  return new Intl.DateTimeFormat(INTL_LOCALE[locale], opts ?? { dateStyle: 'medium' }).format(date);
}

export function formatDateTime(value: string | Date, locale: Locale): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  return new Intl.DateTimeFormat(INTL_LOCALE[locale], { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

export function formatNumber(value: number | string, locale: Locale, opts?: Intl.NumberFormatOptions): string {
  const n = typeof value === 'string' ? Number(value) : value;
  return new Intl.NumberFormat(INTL_LOCALE[locale], opts).format(n);
}

/**
 * `amount CODE` (e.g. "199.00 EUR" / "199,00 EUR"), never a currency symbol
 * or locale-flipped ordering - this repo's own established display
 * convention (a multi-currency B2B ledger reads better as an explicit code
 * than a symbol that can't disambiguate). Locale only changes the NUMBER's
 * own formatting (decimal/group separators), not the amount-then-code shape.
 */
export function formatCurrencyMinor(amountMinor: number | string, currency: string, locale: Locale): string {
  const n = (typeof amountMinor === 'string' ? Number(amountMinor) : amountMinor) / 100;
  const formatted = new Intl.NumberFormat(INTL_LOCALE[locale], { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
  return `${formatted} ${currency}`;
}
